import { Polygon } from 'geojson';
import { observer } from 'mobx-react-lite';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useDrawMapStore, useMapStore } from '../..';
import BaseMap from '../../interfaces/BaseMap';
import { satelliteScreenshotFilename } from '../../interfaces/IAttachment';
import GeometryService from '../../services/geometryService';
import { base64ToFile, formatFileSize } from '../../utils/utils';
import MapThumbnail from '../MapThumbnail';
import HybridMapboxBaseMapLayer from '../baseMapLayers/HybridMapboxBaseMapLayer';
import SatelliteArcGISBaseMapLayer from '../baseMapLayers/SatelliteArcGISBaseMapLayer';
import SatelliteBingBaseMapLayer from '../baseMapLayers/SatelliteBingBaseMapLayer';
import SatelliteGoogleBaseMapLayer from '../baseMapLayers/SatelliteGoogleBaseMapLayer';
import SatelliteMapboxBaseMapLayer from '../baseMapLayers/SatelliteMapboxBaseMapLayer';
import SatelliteNAIPBaseMapLayer from '../baseMapLayers/SatelliteNAIPBaseMapLayer';
import Spinner from '../spinners/Spinner';
import styles from './DealSatelliteScreenshot.module.css';

function DealSatelliteScreenshot({ onScreenshotReady }: IDealSatelliteScreenshotProps) {
    const drawMapStore = useDrawMapStore();
    const mapStore = useMapStore();
    const geometryService = useMemo(() => new GeometryService(), []);
    const [screenshotFile, setScreenshotFile] = useState<File | null>(null);

    const currentShapes = useMemo(() =>
        drawMapStore.result?.features.map(f => geometryService.mapPolygonToShape(f.geometry as Polygon)) ?? null,
    [drawMapStore.result]);

    const currentShapesKey = useMemo(() => JSON.stringify(currentShapes), [currentShapes]);

    useEffect(() => {
        setScreenshotFile(null);
        onScreenshotReady(null);
    }, [currentShapesKey]);

    useEffect(() => {
        if (screenshotFile) {
            setScreenshotFile(null);
            onScreenshotReady(null);
        }
    }, [mapStore.baseMap]);

    const handleScreenshotCaptured = (base64: string) => {
        const file = base64ToFile(base64, satelliteScreenshotFilename);
        setScreenshotFile(file);
        onScreenshotReady(file);
    };

    return (
        <>
            <div className={styles.container}>
                <div className={styles.info}>
                    <div className={styles.header}>{satelliteScreenshotFilename}</div>
                    <div>
                        {screenshotFile
                            ? formatFileSize(screenshotFile.size)
                            : <span className={styles.spinnerContainer}>Loading screenshot...<Spinner className={styles.spinner} /></span>
                        }
                    </div>
                </div>
            </div>
            {!screenshotFile && currentShapes && (
                <MapThumbnail key={mapStore.baseMap} id={`dealSatelliteScreenshot_${mapStore.baseMap}`} shapes={currentShapes} onBase64ScreenshotIsReady={handleScreenshotCaptured}>
                    {(isStyleLoaded) => getSatelliteLayer(mapStore.baseMap, isStyleLoaded)}
                </MapThumbnail>
            )}
        </>
    );
}

export default observer(DealSatelliteScreenshot);

interface IDealSatelliteScreenshotProps {
    onScreenshotReady: (file: File | null) => void;
}

function getSatelliteLayer(baseMap: BaseMap, isStyleLoaded: boolean): ReactNode {
    switch (baseMap) {
        case BaseMap.SatelliteGoogle:
            return <SatelliteGoogleBaseMapLayer visible isStyleLoadedOverride={isStyleLoaded} />;
        case BaseMap.SatelliteArcGIS:
            return <SatelliteArcGISBaseMapLayer visible isStyleLoadedOverride={isStyleLoaded} />;
        case BaseMap.SatelliteBing:
            return <SatelliteBingBaseMapLayer visible isStyleLoadedOverride={isStyleLoaded} />;
        case BaseMap.SatelliteNAIP:
            return <SatelliteNAIPBaseMapLayer visible isStyleLoadedOverride={isStyleLoaded} />;
        case BaseMap.HybridMapbox:
            return <HybridMapboxBaseMapLayer visible isStyleLoadedOverride={isStyleLoaded} />;
        default:
            return <SatelliteMapboxBaseMapLayer visible isStyleLoadedOverride={isStyleLoaded} />;
    }
}
